import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Localization from "expo-localization";
import { setActiveContentLocale } from "@src/packages/content";
import { SUPPORTED_LOCALES, type Locale } from "./locale";
import { makeTFunc, type TranslationFunction } from "./translations";
import en from "./locales/en.json";
import fr from "./locales/fr.json";

const STORAGE_KEY = "louise.locale";
const TRANSLATIONS: Record<Locale, object> = { en, fr };

function isSupportedLocale(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function detectDeviceLocale(): Locale {
  try {
    const code = Localization.getLocales()[0]?.languageCode;
    if (isSupportedLocale(code)) return code;
  } catch {
    // Fall through to default
  }
  return "en";
}

export type I18nContextValue = {
  locale: Locale;
  setLocale: (next: Locale) => Promise<void>;
  t: TranslationFunction;
};

export const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectDeviceLocale);

  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEY)
      .then((stored) => {
        if (isSupportedLocale(stored)) {
          setLocaleState(stored);
        }
      })
      .catch(() => {});
  }, []);

  // Mirror locale into the content registry synchronously during render so
  // descendants (e.g. the Learn list) that derive content via getBlock()/
  // getBlocksByTag() in their own render read the new-locale variant in the
  // same pass. As an effect this ran after commit, so on a live language
  // switch children had already memoized stale previous-locale content and
  // wouldn't re-derive until a remount/reload. The setter is an idempotent
  // module-level write, so calling it during render is safe.
  setActiveContentLocale(locale);

  const setLocale = useCallback(async (next: Locale) => {
    // Update the content registry locale synchronously, before the state change
    // triggers a re-render. The [locale] effect below also mirrors it, but that
    // runs after commit — too late for consumers that re-resolve content blocks
    // during the same render, which would otherwise show the previous language
    // until the next remount.
    setActiveContentLocale(next);
    setLocaleState(next);
    await AsyncStorage.setItem(STORAGE_KEY, next);
  }, []);

  const t = useMemo(() => makeTFunc(TRANSLATIONS[locale]), [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
